<template>
  <div class="q-pa-sm">
    <q-card flat bordered>
      <div class="row q-pa-md">
        <div class="col-12 col-sm-2">
          <div
            class="text-body1 text-weight-bold q-pb-sm"
            :class="$q.dark.mode ? '' : 'text-primary '"
          >
            Broker {{ selectedBroker }} {{ title }} of {{ symbol }}
          </div>
        </div>

        <div class="col-12 col-sm-2 q-pr-sm">
          <q-btn
            v-if="isPlatinum"
            color="secondary"
            outlined
            size="sm"
            label="export csv"
            @click="exportToCSV()"
          />
        </div>

        <div class="col-12 col-sm-2 q-pr-sm">
          <q-select
            :menu-absolute="true"
            outlined
            class="q-mb-md"
            :class="$q.dark.mode ? '' : 'bg-grey-1'"
            v-model.lazy="selectedBroker"
            single
            dense
            clearable
            use-input
            input-debounce="0"
            :options="options"
            option-value="broker_no"
            option-label="broker_name"
            emit-value
            stack-label
            map-options
            label="Select broker"
            filter
            @filter="filterFn"
            @update:model-value="loadData(fromDate, toDate, symbol)"
          >
            <template v-slot:option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section avatar>
                  <q-item-label class="text-bold">{{
                    scope.opt.broker_no
                  }}</q-item-label>
                </q-item-section>
                <q-item-section>
                  <q-item-label>
                    <div class="row">
                      {{ scope.opt.broker_name }}
                    </div>
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
            <template v-slot:no-option>
              <q-item>
                <q-item-section class="text-grey"> No results </q-item-section>
              </q-item>
            </template>
          </q-select>
        </div>

        <div class="col-12 col-sm-3 q-pr-sm">
          <q-input
            dense
            filled
            v-model="fromDate"
            capslock
            mask="date"
            :rules="['date']"
            class="text-bold"
          >
            <q-popup-proxy
              ref="qDateProxy"
              transition-show="scale"
              transition-hide="scale"
            >
              <q-date
                v-model="fromDate"
                @input="() => $refs.qDateProxy.hide()"
                @update:model-value="loadData(fromDate, toDate, symbol)"
              />
            </q-popup-proxy>
            <q-icon
              name="event"
              class="text-grey text-h6 date-icon cursor-pointer q-pt-md"
            />
          </q-input>
        </div>

        <div class="col-12 col-sm-3 q-pr-sm">
          <q-input
            dense
            filled
            v-model="toDate"
            capslock
            mask="date"
            :rules="['date']"
            class="text-bold"
          >
            <q-popup-proxy
              ref="qDateProxy"
              transition-show="scale"
              transition-hide="scale"
            >
              <q-date
                v-model="toDate"
                @input="() => $refs.qDateProxy.hide()"
                @update:model-value="loadData(fromDate, toDate, symbol)"
              />
            </q-popup-proxy>
            <q-icon
              name="event"
              class="text-grey text-h6 date-icon cursor-pointer q-pt-md"
            />
          </q-input>
        </div>
      </div>

      <PackageAuth packageAuthorization="Diamond+">
        <template #authorized-content>
          <div class="q-pa-xs">
            <q-table
              :class="
                $q.dark.mode
                  ? 'table-sticky-header-column-dark'
                  : 'table-column-color-tnx'
              "
              flat
              bordered
              :rows="brokerTransaction"
              separator="cell"
              :columns="columns"
              :pagination="pagination"
            >
              <template v-slot:bottom-row>
                <q-tr class="text-bold">
                  <q-td colspan="1">Total</q-td>
                  <q-td></q-td>
                  <q-td></q-td>
                  <q-td class="text-center">
                    {{ sumBy(brokerTransaction, "buy_quantity") }}
                  </q-td>
                  <q-td></q-td>
                  <q-td class="text-center">
                    {{ sumBy(brokerTransaction, "buy_amount").toFixed(2) }}
                  </q-td>
                  <q-td class="text-center">
                    {{ sumBy(brokerTransaction, "sell_quantity") }}
                  </q-td>
                  <q-td></q-td>
                  <q-td class="text-center">
                    {{ sumBy(brokerTransaction, "sell_amount").toFixed(2) }}
                  </q-td>
                  <q-td class="text-center">
                    <!-- ..........{{ brokerTransaction }} -->
                    {{
                      brokerTransaction.reduce(
                        (sum, transaction) =>
                          sum + transaction.net_quantity || 0,
                        0
                      )
                    }}
                    <!-- {{ sumBy(brokerTransaction, "net_quantity") }} -->
                  </q-td>
                  <q-td class="text-center">
                    {{ netCumulativeQuantity }}
                    <!-- {{ sumBy(brokerTransaction, "net_quantity") }} -->
                  </q-td>
                  <q-td class="text-center">
                    <!-- {{ sumBy(brokerTransaction, "net_amount").toFixed(2) }} -->
                    {{
                      brokerTransaction
                        .reduce(
                          (sum, transaction) =>
                            sum + transaction.net_amount || 0,
                          0
                        )
                        .toFixed(2)
                    }}
                  </q-td>
                </q-tr>
              </template>
            </q-table>
            <q-card flat bordered class="q-pa-xs q-mt-sm">
              <div
                class="text-body1 text-weight-bold q-pa-sm"
                :class="$q.dark.mode ? '' : 'text-primary '"
              >
                Broker Holding Chart
              </div>

              <div class="q-gutter-sm">
                <q-radio
                  v-model="selectFilter"
                  val="buy-quantity"
                  label="Buy Quantity"
                />
                <q-radio
                  v-model="selectFilter"
                  val="sell-quantity"
                  label="Sell Quantity"
                />
                <q-radio
                  v-model="selectFilter"
                  val="net-quantity"
                  label="Net Quantity"
                />
                <q-radio
                  v-model="selectFilter"
                  val="cumulative-quantity"
                  label="Cumulative Quantity"
                />
              </div>
              <q-separator spaced />

              <q-card flat bordered class="q-pa-md">
                <VueApexCharts
                  :options="chartOptions"
                  :series="series"
                  type="line"
                  height="435"
                />
              </q-card>
            </q-card>
          </div>
        </template>
      </PackageAuth>
    </q-card>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from "vue";
import { useQuasar } from "quasar";
import { useStore } from "vuex";

import useData from "src/use/data";
import PackageAuth from "src/components/Packages/PackageAuth.vue";

import VueApexCharts from "vue3-apexcharts";
import { sumBy } from "lodash";

const props = defineProps({
  symbol: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
    default: "Holding",
  },
});

const store = useStore();
const $q = useQuasar();

const { netHoldingDateWise, netReleasedDateWise, brokerList } = useData();

const fromDate = ref("");
const toDate = ref("");

let selectedBroker = ref(35);
const brokerLists = ref([]);
const options = ref([]);

let topBuyDaily = ref(null);
let topSellDaily = ref(null);
const selectFilter = ref("cumulative-quantity");
let netCumulativeQuantity = ref(0);
const isPlatinum = ref(false);

async function loadData(fromDate, toDate, symbol) {
  $q.loading.show({});
  try {
    const [buyerRes, sellerRes] = await Promise.all([
      netHoldingDateWise(
        fromDate.replaceAll("/", "-"),
        toDate.replaceAll("/", "-"),
        symbol,
        selectedBroker.value
      ),
      netReleasedDateWise(
        fromDate.replaceAll("/", "-"),
        toDate.replaceAll("/", "-"),
        symbol,
        selectedBroker.value
      ),
    ]);
    topBuyDaily.value = buyerRes.data.filter((item) => item.quantity > 0);
    topSellDaily.value = sellerRes.data.filter((item) => item.quantity > 0);
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    $q.loading.hide();
  }
}

const brokerTransaction = computed(() => {
  if (!topBuyDaily.value || !topSellDaily.value) {
    return [];
  }

  // Create maps for buyData and sellData for faster lookup
  const buyDataMap = new Map(
    topBuyDaily.value.map((item) => [item.date, item])
  );
  const sellDataMap = new Map(
    topSellDaily.value.map((item) => [item.date, item])
  );

  // Combine all unique dates from both buyData and sellData
  const allDates = new Set([...buyDataMap.keys(), ...sellDataMap.keys()]);

  // Convert Set to array and sort dates chronologically
  const sortedDates = [...allDates].sort();
  let cumulativeQuantity = 0;

  // Construct final data for each date
  return sortedDates.map((date) => {
    const buyItem = buyDataMap.get(date);
    const sellItem = sellDataMap.get(date);

    const buyQuantity = buyItem ? buyItem.quantity : 0;
    const buyAmount = buyItem ? buyItem.amount : 0;
    const sellQuantity = sellItem ? sellItem.quantity : 0;
    const sellAmount = sellItem ? sellItem.amount : 0;
    let netQuantity = buyQuantity - sellQuantity;
    // if (netQuantity < 0) netQuantity = 0;
    cumulativeQuantity += netQuantity || 0;
    if (cumulativeQuantity < 0) {
      cumulativeQuantity = 0;
    }
    netCumulativeQuantity.value = cumulativeQuantity;

    // Construct the final object
    return {
      date,
      symbol: buyItem ? buyItem.symbol : sellItem ? sellItem.symbol : null,
      broker: buyItem ? buyItem.buyer : sellItem ? sellItem.seller : null,
      buy_quantity: buyQuantity,
      buy_rate: buyItem ? buyItem.rate : 0,
      buy_amount: buyAmount,
      sell_quantity: sellQuantity,
      sell_rate: sellItem ? sellItem.rate : 0,
      sell_amount: sellAmount,
      net_quantity: netQuantity,
      cumulative_quantity: cumulativeQuantity,
      net_amount: buyAmount - sellAmount,
    };
  });
});

const columns = [
  {
    name: "date",
    required: true,
    label: "Date",
    field: "date",
    sortable: true,
    format: (val, row) => {
      return val;
    },
    align: "left",
  },
  {
    name: "symbol",
    required: true,
    label: "Symbol",
    field: "symbol",
    sortable: true,
    format: (val, row) => {
      return val;
    },
    align: "center",
  },
  {
    name: "broker",
    required: true,
    label: "Broker",
    field: "broker",
    format: (val, row) => {
      return val;
    },
    align: "center",
  },
  {
    name: "buy_quantity",
    required: true,
    label: "Buy Quantity",
    field: "buy_quantity",
    sortable: true,
    format: (val, row) => {
      return val;
    },
    align: "center",
  },
  {
    name: "buy_rate",
    required: true,
    label: "Buy Rate",
    field: "buy_rate",
    sortable: true,
    format: (val, row) => {
      return val?.toFixed(2);
    },
    align: "center",
  },
  {
    name: "buy_amount",
    required: true,
    label: "Buy Amount",
    field: "buy_amount",
    sortable: true,
    format: (val, row) => {
      return val?.toFixed(2);
    },
    align: "center",
  },
  {
    name: "sell_quantity",
    required: true,
    label: "Sell Quantity",
    field: "sell_quantity",
    sortable: true,
    format: (val, row) => {
      return val;
    },
    align: "center",
  },
  {
    name: "sell_rate",
    required: true,
    label: "Sell Rate",
    field: "sell_rate",
    sortable: true,
    format: (val, row) => {
      return val?.toFixed(2);
    },
    align: "center",
  },
  {
    name: "sell_amount",
    required: true,
    label: "Sell Amount",
    field: "sell_amount",
    sortable: true,
    format: (val, row) => {
      return val?.toFixed(2);
    },
    align: "center",
  },
  {
    name: "net_quantity",
    required: true,
    label: "Net Holding",
    field: "net_quantity",
    sortable: true,
    format: (val, row) => {
      return val || 0;
    },
    style: (row) => {
      return row.net_quantity == null
        ? ""
        : parseFloat(row.net_quantity) > 0
        ? "color:green;"
        : "color:red;";
    },
    align: "center",
  },
  {
    name: "cumulative_quantity",
    required: true,
    label: "Cumm Quantity",
    field: "cumulative_quantity",
    sortable: true,
    format: (val, row) => {
      return val || 0;
    },
    style: (row) => {
      return row.cumulative_quantity == null
        ? ""
        : parseFloat(row.cumulative_quantity) > 0
        ? "color:green;"
        : "color:red;";
    },
    align: "center",
  },
  {
    name: "net_amount",
    required: true,
    label: "Net Amount",
    field: "net_amount",
    sortable: true,
    format: (val, row) => {
      return val ? val.toFixed(0) : 0 || 0;
    },
    style: (row) => {
      return row.net_amount == null
        ? ""
        : parseFloat(row.net_amount) > 0
        ? "color:green;"
        : "color:red;";
    },
    align: "center",
  },
];

const pagination = ref({
  sortBy: "date",
  descending: false,
  rowsPerPage: 15,
});

const filterFn = (val, update) => {
  if (val === "") {
    update(() => {
      options.value = brokerLists.value;
    });
    return;
  }

  update(() => {
    const needle = val.toLowerCase();
    options.value = brokerLists.value.filter(
      (v) =>
        v.broker_name.toLowerCase().indexOf(needle) > -1 ||
        v.broker_no.toString().toLowerCase().indexOf(needle) > -1
    );
  });
};

watch(props, (newVal, oldVal) => {
  loadData(fromDate.value, toDate.value, newVal.symbol);
});

const calculateFromDate = (toDate) => {
  const date = new Date(toDate);
  date.setDate(date.getDate() - 15);
  return date.toISOString().substring(0, 10);
};

onMounted(async () => {
  try {
    if (store.state.market.asOf) {
      toDate.value = store.state.market.asOf;
      fromDate.value = calculateFromDate(toDate.value);
    } else {
      store.dispatch("market/marketStatusNew").then(() => {
        toDate.value = store.state.market.asOf;
        fromDate.value = calculateFromDate(toDate.value);
      });
    }

    if (store.state.authenticate.isAuthenticated) {
      store.dispatch("packages/getPackageStatus").then(() => {
        if (
          store.state.packages.packageStatus?.current_package?.package_name ===
          "Platinum"
        ) {
          isPlatinum.value = true;
        }
      });
    }

    const res = await brokerList();
    brokerLists.value = res.data;

    selectedBroker.value = 58;
    await loadData(fromDate.value, toDate.value, props.symbol);
  } catch (error) {
    console.error("Error:", error);
  }
});

const series = computed(() => {
  let data;

  switch (selectFilter.value) {
    case "cumulative-quantity":
      data = brokerTransaction.value.map((item) => item.cumulative_quantity);
      break;
    case "buy-quantity":
      data = brokerTransaction.value.map((item) => item.buy_quantity);
      break;
    case "sell-quantity":
      data = brokerTransaction.value.map((item) => item.sell_quantity);
      break;
    case "net-quantity":
      data = brokerTransaction.value.map((item) => item.net_quantity);
      break;
    default:
      data = [];
  }

  return [
    {
      name: selectFilter.value
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      type: "line",
      data: data,
    },
    // {
    //   name: "Amount",
    //   type: "column",
    //   data: brokerTransaction.value.map((item) => item.net_amount),
    // },
  ];
});

const chartOptions = computed(() => ({
  chart: {
    height: 435,
    type: "line",
    zoom: {
      enabled: false,
      type: "x",
      autoScaleYaxis: true,
    },
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: "smooth",
    width: 2,
  },
  legend: {
    horizontalAlign: "left",
  },
  title: {
    text: `Broker ${selectedBroker.value} ${props.symbol} Holding `,
    style: {
      fontSize: "13px",
      color: $q.dark.mode === true ? "#fff" : "#000",
    },
  },
  tooltip: $q.dark.mode === true ? { theme: "dark" } : { theme: "light" },
  dataLabels: {
    enabled: true,
    enabledOnSeries: [0],
  },
  legend: {
    show: true,
    position: "bottom",
    labels: {
      colors: $q.dark.mode ? "#fff" : "#000",
    },
  },
  xaxis: {
    type: "datetime",
    categories: brokerTransaction.value.map((item) => item.date),
    labels: {
      style: {
        colors: $q.dark.mode === true ? "#fff" : "#000",
      },
    },
  },
  yaxis: [
    {
      opposite: true,
      title: {
        text: selectFilter.value
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
      },
      labels: {
        formatter: function (val) {
          return val?.toFixed(1);
        },
        style: {
          colors: $q.dark.mode === true ? "#fff" : "#000",
        },
      },
    },
    // {
    //   title: {
    //     text: "Amount",
    //   },
    //   labels: {
    //     formatter: function (val) {
    //       return val?.toFixed(1);
    //     },
    //     style: {
    //       colors: $q.dark.mode === true ? "#fff" : "#000",
    //     },
    //   },
    // },
  ],
}));

const exportToCSV = () => {
  const data = brokerTransaction.value;

  if (!data || data.length === 0) {
    console.warn("No data available to export.");
    return;
  }

  const dimensions = Object.keys(data[0]);

  const csvData = data.map((item) =>
    dimensions.map((dimension) => item[dimension] || "-")
  );

  csvData.unshift(dimensions);

  csvData.unshift(["chukul.com"]);

  const csvString = csvData.map((row) => row.join(",")).join("\n");

  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });

  const url = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = "broker-transaction-export.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
</script>
